export type Constructor<T = any> = new (...args: any[]) => T
export type AbstractConstructor<T = any> = abstract new (...args: any[]) => T
export type ConstructorOrAbstractConstructor = Constructor | AbstractConstructor

const constructorSkipProperties = ['length', 'name', 'prototype'] as const
const constructorSkipPropertiesSet = new Set(constructorSkipProperties)
const prototypeSkipProperties = ['constructor'] as const
const prototypeSkipPropertiesSet = new Set(prototypeSkipProperties)

function trace(msg: string): void {
  const enable = false
  if (enable) {
    console.log(msg)
  }
}

function copyMethods(from: any, to: any, skip?: Set<string>): void {
  for (const name of Object.getOwnPropertyNames(from)) {
    // Do not overwrite properties which we want to skip.
    if (skip?.has(name)) {
      continue
    }

    const descriptor = Object.getOwnPropertyDescriptor(from, name)
    if (descriptor !== undefined) {
      trace(`Copying property "${name}"...`)
      Object.defineProperty(to, name, descriptor)
    }
  }
}

function eraseMethods(obj: any): void {
  for (const name of Object.getOwnPropertyNames(obj)) {
    const descriptor = Object.getOwnPropertyDescriptor(obj, name)
    if (descriptor !== undefined) {
      trace(`Deleting property "${name}"...`)
      delete obj[name]
    }
  }
}

/**
 * Returns true if `derivedCtor` is a class which extends `baseCtor`.
 */
function doesExtendBaseClass(
  baseCtor: ConstructorOrAbstractConstructor,
  derivedCtor: ConstructorOrAbstractConstructor
): boolean {
  return Object.getPrototypeOf(derivedCtor.prototype) === baseCtor.prototype
}

/**
 * If we need to insert a prototype in the prototype chain above
 * `baseCtor.prototype` in order to support super calls to base from one of
 * the mixins (ie: a mixin extends base), create and return such a prototype
 * object.
 */
function createBasePrototype(ctor: ConstructorOrAbstractConstructor): object {
  trace(`Inserting a prototype to hold methods from "${ctor.name}"...`)
  const basePrototype = Object.create(Object.getPrototypeOf(ctor.prototype))
  copyMethods(ctor.prototype, basePrototype)

  trace(`Deleting existing methods from "${ctor.name}"...`)
  eraseMethods(ctor.prototype)
  return basePrototype
}

/**
 * Changing the prototype chain for a class invalidates the target of `super`
 * calls. If the mixin extends the base class, for example, and we will be
 * changing the base class prototype chain then we need to also update the
 * mixin prototype chain.
 */
function updateMixinPrototype(basePrototype: object, mixinCtor: ConstructorOrAbstractConstructor): void {
  trace(`Updating the prototype chain for "${mixinCtor.name}"...`)
  Object.setPrototypeOf(mixinCtor.prototype, basePrototype)
}

/**
 * Apply a set of mixins to `baseCtor` such that it gains methods from the
 * mixins in-place. That-is, without needing to overwrite the class declaration
 * of `baseCtor`.
 *
 * Supports mixins which extend `baseCtor` and any `super` keyword usage by
 * inserting a prototype object to act as the `super` target in the chain just
 * above `baseCtor.prototype`.
 *
 * Supports method overriding for mixins which extend `baseCtor` via the above
 * mechanism.
 *
 * Note: The class methods are copied from mixins onto `baseCtor.prototype` in
 * order of mixin constructors in `mixinCtors`. This means if multiple mixins
 * contain a method with the same name, the last one in the array (by index)
 * wins and overwrites all others.
 *
 * For the simple case of an arbitrary mixin:
 * ```ts
 *   class Base {
 *     public foo(): void {}
 *   }
 *
 *   class Mixin {
 *     public bar(): void {]
 *   }
 * ```
 *
 * We copy the `bar()` method from `Mixin.prototype` onto `Base.prototype` and
 * that's all. New instances of `Base` will automatically have access to `bar()`.
 *
 * For the case of a mixin which extends from the base target class:
 * ```ts
 *   class Base {
 *     public foo(): void {}
 *   }
 *
 *   class Derived extends Base {
 *     public bar(): void {}
 *   }
 * ```
 *
 * The classes `Base` and `Derived` create a diagram like this:
 *```
 *   %ObjectPrototype%        %FunctionPrototype%
 *          /\                        /\
 *          || __proto__              || __proto__
 *          ||                        ||
 *       prototype <================ Base
 *          /\                        /\
 *          || __proto__              || __proto__
 *          ||                        ||
 *       prototype <============== Derived
 *```
 * That is to say:
 * ```ts
 * Derived.prototype.__proto__ === Base.prototype // true
 * Derived.__proto__ === Base // true
 * Base.prototype.__proto__ === Object.prototype // true
 * Base.__proto__ === Function.prototype // true
 * ```
 *
 * If we want to apply the mixins `[Derived]` back onto the `Base` class,
 * we do that by creating a new prototype object which has `__proto__` set to
 * `Base.prototype.__proto__` and copying all of the methods from
 * `Base.prototype` onto that new mixin prototype. It will act as the target
 * of `super` calls within mixin members. Then we copy the mixin methods from
 * `Derived.prototype` onto `Base.prototype` as usual. Finally, we need to
 * repoint the prototype of `Derived.prototype` to the new mixin prototype
 * object so that super calls within `Derived` continue to work.
 *
 * Results in this:
 *```
 *        %ObjectPrototype%        %FunctionPrototype%
 *                 /\                        /\
 *                 || __proto__              ||
 *                 ||                        ||
 *         mixinPrototype                    || __proto__
 *          /\        /\                     ||
 *          ||        || __proto__           ||
 *          ||        ||                     ||
 *          ||     prototype <============= Base
 *          ||                               /\
 *          || __proto__                     || __proto__
 *          ||                               ||
 *       prototype <===================== Derived
 *```
 */
export function applyMixins(
  baseCtor: ConstructorOrAbstractConstructor,
  mixinCtors: ConstructorOrAbstractConstructor[]
): void {
  trace(`Applying mixins to "${baseCtor.name}":`)
  let mixinPrototype: object | null = null

  for (const mixinCtor of mixinCtors) {
    const doesMixinExtendBase = doesExtendBaseClass(baseCtor, mixinCtor)
    if (doesMixinExtendBase && mixinPrototype === null) {
      mixinPrototype = createBasePrototype(baseCtor)
    }

    const targetPrototype = !doesMixinExtendBase && mixinPrototype !== null ? mixinPrototype : baseCtor.prototype

    trace(`Applying static methods from "${mixinCtor.name}" to "${baseCtor.name}"...`)
    copyMethods(mixinCtor, baseCtor, constructorSkipPropertiesSet)

    trace(`Applying instance methods from "${mixinCtor.name}"...`)
    copyMethods(mixinCtor.prototype, targetPrototype, prototypeSkipPropertiesSet)

    if (doesMixinExtendBase && mixinPrototype !== null) {
      updateMixinPrototype(mixinPrototype, mixinCtor)
    }
  }

  if (mixinPrototype !== null) {
    trace(`Updating the prototype chain for "${baseCtor.name}"...`)
    Object.setPrototypeOf(baseCtor.prototype, mixinPrototype)
  }
}
